import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  CheckCheck,
  CalendarOff,
  PartyPopper,
  AlertTriangle,
  Search,
  Eye,
  LayoutGrid,
  Lock,
  RefreshCcw,
  Calculator,
} from "lucide-react";
import { toast } from "sonner";
import PreviewFinalizeModal from "@/components/PreviewFinalizeModal";

const GRADE_COLOR = {
  P: "bg-green-100 text-green-800 border-green-200",
  H: "bg-amber-100 text-amber-800 border-amber-200",
  A: "bg-red-100 text-red-800 border-red-200",
};

const fmtMin = (m) => {
  if (m == null) return "—";
  const sign = m < 0 ? "-" : "";
  m = Math.abs(m);
  return `${sign}${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

const TYPE_LABEL = {
  white_collar: "Admin",
  blue_collar: "Blue Collar",
  driver: "Driver",
  manual_entry: "Manual",
};

function computeRow(emp, days, employeesById) {
  const counted = days.filter((d) => !d.is_off);
  const p = counted.filter((d) => d.grade === "P").length;
  const h = counted.filter((d) => d.grade === "H").length;
  const a = counted.filter((d) => d.grade === "A").length;
  const otMin = counted.reduce((s, d) => s + (d.ot_minutes || 0), 0);
  const dayCount = p * 1.0 + h * 0.5;
  const sh = Number(emp.shift_hours) || 9;
  // OT count is computed for any OT-applicable employee (decoupled from category).
  // Negative OT (under-shift days) flows through; no clamping at the row level.
  const otCount = emp.overtime_applicable
    ? Math.round(((otMin / 60) / sh) * 100) / 100
    : 0;
  // Late count (white-collar only)
  let lateCount = 0;
  if (emp.category === "white_collar" && emp.shift_start) {
    const [hh, mm] = emp.shift_start.split(":").map(Number);
    const ss = hh * 60 + mm;
    const threshold = ss + 15;
    lateCount = counted.filter(
      (d) => d.grade === "P" && d.first_in_min != null && d.first_in_min > threshold
    ).length;
  }
  const lateDed = Math.max(0, lateCount - 8) * 0.5;
  return { p, h, a, otMin, dayCount, otCount, lateCount, lateDed };
}

const _daysInMonth = (monthIso) => {
  // monthIso like "2026-05-01" → 31. Defaults to 31 if unknown so we
  // don't accidentally drop day 31 columns ever again.
  if (!monthIso) return 31;
  try {
    const m = new Date(monthIso);
    return new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  } catch {
    return 31;
  }
};

export default function AttendanceReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("clean"); // clean | grid
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | anomaly | override
  const [editing, setEditing] = useState(null); // {emp, day, daily}
  const [markDay, setMarkDay] = useState(null); // {day}
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Dynamic day-columns based on the upload's month — was previously a
  // module-level constant of length 30 which silently dropped day 31 from
  // Raw Grid + Mark-day dropdown for Jan/Mar/May/Jul/Aug/Oct/Dec.
  const dayCols = useMemo(() => {
    const n = _daysInMonth(data?.upload?.month);
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [data?.upload?.month]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/uploads/${id}/daily`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const employees = useMemo(() => {
    if (!data) return [];
    let list = data.employees;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) =>
        [e.name, e.punch_id, e.employee_code].some((v) => v?.toLowerCase().includes(q))
      );
    }
    if (filter === "anomaly") {
      list = list.filter((e) => Object.values(e.days).some((d) => d.is_anomaly));
    } else if (filter === "override") {
      list = list.filter((e) => Object.values(e.days).some((d) => d.is_override));
    }
    return list;
  }, [data, search, filter]);

  const empById = useMemo(() => Object.fromEntries((data?.employees || []).map((e) => [e.employee_id, e])), [data]);

  const onSaveOverride = async (payload) => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.patch(`/attendance/daily/${editing.daily.id}`, payload);
      toast.success("Override saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onMarkDay = async (mode, reason) => {
    if (!markDay) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/attendance/uploads/${id}/mark-day`, {
        day: markDay.day,
        mode,
        reason,
      });
      toast.success(`${res.updated} cells updated`);
      setMarkDay(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onFinalize = async () => {
    if (!window.confirm("Finalize this upload? Monthly summary will be computed and the upload will be locked.")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/attendance/uploads/${id}/finalize`);
      toast.success(`Finalized — ${res.summary_rows} summary rows`);
      navigate(`/attendance/monthly?upload_id=${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-slate-500">Loading review...</div>;
  }
  const upload = data.upload;
  const isCommitted = upload.status === "committed";
  // Phase 5T.3 — super_admin can override even after a run is committed
  // (with audit trail). HR is locked once committed.
  const editLocked = isCommitted && !isSuperAdmin;
  // Compute weekday for each day-of-month based on the upload's month.
  // This drives the Sun/Mon/Tue label below each day number AND the Sunday
  // visual highlight, removing the year/month confusion that plagued the
  // legacy grid (where users couldn't tell which numeric day was a Sunday).
  const monthDate = upload?.month ? new Date(upload.month) : null;
  const dayWeek = (d) => {
    if (!monthDate || isNaN(monthDate.getTime())) return null;
    const dt = new Date(monthDate.getFullYear(), monthDate.getMonth(), d);
    const wkd = dt.getDay(); // 0=Sun...6=Sat
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return { idx: wkd, label: labels[wkd] };
  };

  return (
    <div data-testid="attendance-review-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/attendance" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Review</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {upload.unit_name} ·{" "}
            {new Date(upload.month).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-mono truncate">{upload.filename}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className="uppercase text-[10px]">
            {upload.matched_count} matched · {upload.unmatched_punch_ids} unmatched
          </Badge>
          <Button
            data-testid="preview-finalize-btn"
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Calculator className="h-4 w-4 mr-2" />
            Preview Calc
          </Button>
          {isCommitted ? (
            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
              <Lock className="h-3 w-3 mr-1" />
              Finalised
            </Badge>
          ) : canEdit ? (
            <>
              <Button
                data-testid="goto-anomalies-btn"
                variant="outline"
                onClick={() => navigate(`/attendance/anomalies/${id}`)}
              >
                Anomaly review
              </Button>
              <Button onClick={onFinalize} disabled={busy} className="brand-bg brand-bg-hover text-white" data-testid="finalize-btn">
                <CheckCheck className="h-4 w-4 mr-2" />
                Finalize → Monthly
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="review-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search punch ID, name, code..."
            className="pl-9"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="review-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            <SelectItem value="anomaly">Anomalies only</SelectItem>
            <SelectItem value="override">Overrides only</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border border-slate-200 rounded-md overflow-hidden">
          <button
            data-testid="view-clean"
            className={`px-3 py-2 text-sm font-medium flex items-center gap-2 ${view === "clean" ? "brand-bg text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            onClick={() => setView("clean")}
          >
            <Eye className="h-4 w-4" /> Clean Report
          </button>
          <button
            data-testid="view-grid"
            className={`px-3 py-2 text-sm font-medium flex items-center gap-2 ${view === "grid" ? "brand-bg text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            onClick={() => setView("grid")}
          >
            <LayoutGrid className="h-4 w-4" /> Raw Grid
          </button>
        </div>
        {canEdit && !editLocked && (
          <Button variant="outline" data-testid="bulk-mark-btn" onClick={() => setMarkDay({ day: 1 })}>
            <PartyPopper className="h-4 w-4 mr-2" />
            Mark day...
          </Button>
        )}
        <Button variant="outline" size="icon" onClick={load} title="Reload">
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>

      {isCommitted && isSuperAdmin && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2" data-testid="superadmin-unlocked-banner">
          <span className="font-medium">Super-admin override mode:</span>
          <span>Run is finalised but you can still edit any cell. All changes are written to the audit log.</span>
        </div>
      )}

      {/* Rev. 2026-08 — Unmatched Punch-IDs red-flag summary. */}
      {Array.isArray(upload.unmatched) && upload.unmatched.length > 0 && (
        <div
          className="rounded-md border-2 border-red-300 bg-red-50 p-4"
          data-testid="unmatched-summary-card"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-red-900">
                  {upload.unmatched.length} unmatched Punch ID{upload.unmatched.length === 1 ? "" : "s"} — not present in Employees master
                </h3>
                <span className="text-xs text-red-700">
                  {upload.matched_count} matched · {upload.total_punch_ids || (upload.matched_count + upload.unmatched.length)} total in CSV
                </span>
              </div>
              <p className="text-xs text-red-800/80 mt-1">
                These employees exist in the biometric export but aren&apos;t in your employees master.
                Fix by either (a) adding the employee (Employees → New) or (b) correcting the
                Punch ID / Biometric ID on the existing employee record so it matches the CSV.
              </p>
              <div className="mt-3 max-h-40 overflow-y-auto border border-red-200 rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-red-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold text-red-900">Punch ID (from CSV)</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-red-900">Name (from CSV)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upload.unmatched.map((u, i) => (
                      <tr
                        key={`${u.punch_id}-${i}`}
                        className="border-t border-red-100"
                        data-testid={`unmatched-row-${u.punch_id}`}
                      >
                        <td className="px-2 py-1.5 font-mono text-red-800">{u.punch_id}</td>
                        <td className="px-2 py-1.5 text-slate-700">{u.raw_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLEAN REPORT */}
      {view === "clean" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clean-report-table">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-3">Punch</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Code</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3 text-right">Shift</th>
                  <th className="px-3 py-3 text-right">P</th>
                  <th className="px-3 py-3 text-right">H</th>
                  <th className="px-3 py-3 text-right">A</th>
                  <th className="px-3 py-3 text-right">Day Count</th>
                  <th className="px-3 py-3 text-right">OT (hrs)</th>
                  <th className="px-3 py-3 text-right">OT count</th>
                  <th className="px-3 py-3 text-right">Late</th>
                  <th className="px-3 py-3 text-right">Late Ded.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map((e) => {
                  const days = Object.values(e.days);
                  const r = computeRow(e, days, empById);
                  return (
                    <tr key={e.employee_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs">{e.punch_id}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{e.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{e.employee_code}</td>
                      <td className="px-3 py-2 text-xs">{TYPE_LABEL[e.category]}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{e.shift_hours}h</td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-700">{r.p}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.h}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-700">{r.a}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.dayCount.toFixed(1)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums text-xs ${r.otMin < 0 ? "text-red-700 font-semibold" : ""}`}>{fmtMin(r.otMin)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.otCount < 0 ? "text-red-700 font-semibold" : ""}`}>{r.otCount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {e.category === "white_collar" ? r.lateCount : "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-700">
                        {e.category === "white_collar" && r.lateDed > 0 ? `-${r.lateDed.toFixed(1)}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RAW GRID */}
      {view === "grid" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: "75vh" }}>
            <table className="text-xs border-collapse" data-testid="raw-grid-table">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left sticky left-0 bg-slate-100 z-20 w-48">
                    Employee
                  </th>
                  <th className="px-2 py-2 text-left bg-slate-100">Field</th>
                  {dayCols.map((d) => {
                    const w = dayWeek(d);
                    const isSunday = w?.idx === 0;
                    const headerBg = isSunday ? "bg-amber-100" : "bg-slate-100";
                    const labelColor = isSunday ? "text-amber-800 font-semibold" : "text-slate-500";
                    return (
                      <th
                        key={d}
                        className={`px-1 py-2 text-center ${headerBg} w-12 border-l border-slate-200`}
                        title={isSunday ? `Sunday — weekly off (full worked time → OT)` : ""}
                      >
                        <button
                          data-testid={`grid-day-header-${d}`}
                          onClick={() => canEdit && !editLocked && setMarkDay({ day: d })}
                          className="hover:underline disabled:cursor-default flex flex-col items-center w-full"
                          disabled={!canEdit || editLocked}
                        >
                          <span className="text-sm">{d}</span>
                          {w && <span className={`text-[9px] uppercase tracking-wider ${labelColor}`}>{w.label}</span>}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {employees.map((e) => {
                  // Late row only renders for Admin (white_collar variants).
                  // Blue collar / Driver / Manual entry don't have grace-late rules.
                  const isAdmin = e.category === "white_collar" || e.category === "white_collar_female";
                  // OT row only renders for OT-applicable employees so the
                  // Raw Grid doesn't waste a row on non-applicable rows.
                  // Fallback: show OT row if shift exists. (Per-day will
                  // still show "—" for non-OT employees.)
                  const rows = [
                    { key: "first_in", label: "First IN" },
                    { key: "last_out", label: "Last Out" },
                    { key: "total_hrs_raw", label: "Total Hrs" },
                    { key: "shift", label: "Shift" },
                    { key: "ot_minutes", label: "OT", hidden: !e.overtime_applicable },
                    { key: "grade", label: "Grade" },
                    { key: "late", label: "Late", hidden: !isAdmin },
                  ].filter((r) => !r.hidden);
                  return (
                    <tbody key={e.employee_id} className="border-t-2 border-slate-300">
                      {rows.map((row, ri) => (
                        <tr key={`${e.employee_id}-${row.key}`} className="border-b border-slate-100">
                          {ri === 0 && (
                            <td
                              rowSpan={rows.length}
                              className="px-2 py-1 sticky left-0 bg-white border-r border-slate-200 align-top z-10 w-48"
                            >
                              <div className="text-xs font-semibold text-slate-900 truncate">{e.name}</div>
                              <div className="text-[10px] text-slate-500 font-mono">{e.punch_id}</div>
                              <div className="text-[10px] text-slate-400">{TYPE_LABEL[e.category]} · {e.shift_hours}h</div>
                              <div className="text-[10px] mt-0.5">
                                <span className={`inline-block px-1.5 py-0.5 rounded ${isAdmin ? "bg-blue-100 text-blue-800" : e.overtime_applicable ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                                  {isAdmin ? "Admin" : (e.overtime_applicable ? "Overtime" : "Standard")}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="px-2 py-1 bg-slate-50 text-[10px] text-slate-600 font-medium uppercase tracking-wider whitespace-nowrap">
                            {row.label}
                          </td>
                          {dayCols.map((d) => {
                            const day = e.days[d];
                            const w = dayWeek(d);
                            const isSunday = w?.idx === 0;
                            const sundayBg = isSunday ? "bg-amber-50/60" : "";
                            if (!day) return <td key={d} className={`px-1 py-1 text-center border-l border-slate-100 ${sundayBg}`}>—</td>;
                            let display = "—";
                            let cls = "";
                            if (row.key === "first_in") display = day.first_in || "—";
                            else if (row.key === "last_out") display = day.last_out || "—";
                            else if (row.key === "total_hrs_raw") display = day.total_hrs_raw || "—";
                            else if (row.key === "shift") display = `${day.shift_hours || ""}`;
                            else if (row.key === "ot_minutes") {
                              display = day.ot_minutes ? fmtMin(day.ot_minutes) : "—";
                              if (day.ot_minutes > 0) cls = "bg-amber-50 text-amber-800 font-medium";
                              else if (day.ot_minutes < 0) cls = "bg-red-50 text-red-700 font-medium";
                            } else if (row.key === "grade") {
                              display = day.grade || "—";
                              cls = day.grade ? GRADE_COLOR[day.grade] : "";
                            } else if (row.key === "late") {
                              // Late count display for Admin/white-collar.
                              // Rules: shift_start + 15 min grace cutoff.
                              //   - On time → "—"
                              //   - Within grace (≤ +15) → running count (1, 2, 3 …)
                              //     after 8 grace lates, each new grace counts as ½.
                              //   - Hard late (> +15) → always ½ regardless of count.
                              // We display: late_index for that day, and a "½"
                              // marker when this day actually deducts a half.
                              const flag = day.late_flag;
                              if (!flag) display = "—";
                              else if (flag === "grace") display = day.late_index ? String(day.late_index) : "•";
                              else if (flag === "grace_deduct") {
                                display = `${day.late_index ?? ""} ½`;
                                cls = "bg-red-50 text-red-700 font-semibold";
                              } else if (flag === "hard") {
                                display = "½";
                                cls = "bg-red-100 text-red-800 font-bold";
                              } else display = "—";
                            }
                            const cellExtra = day.is_anomaly ? "ring-2 ring-red-300 ring-inset" : "";
                            const overrideExtra = day.is_override ? "border-l-2 border-blue-400" : "";
                            const offHolidayExtra = day.is_holiday ? "bg-purple-50" : day.is_off ? "bg-slate-100 text-slate-400" : "";
                            const sandwichExtra = (day.is_sandwich || day.is_zero_attendance_sunday || day.is_excess_leave_deduction) && row.key === "grade" ? "bg-red-100 text-red-900 font-bold" : "";
                            const sundayMarkers = [];
                            if (day.is_zero_attendance_sunday) sundayMarkers.push("Zero-att Sunday → A");
                            if (day.is_sandwich) sundayMarkers.push("Sandwich rule → A");
                            if (day.is_excess_leave_deduction) sundayMarkers.push("Excess leave deduction → A");
                            if (day.is_holiday) sundayMarkers.push("Holiday");
                            const markerTitle = sundayMarkers.length ? sundayMarkers.join(" · ") : "";
                            const finalTitle = day.is_anomaly
                              ? day.anomaly_reason
                              : (day.is_override ? `Override: ${day.override_reason}` : markerTitle);
                            return (
                              <td
                                key={d}
                                onClick={() => canEdit && !editLocked && setEditing({ emp: e, day: d, daily: day })}
                                className={`px-1 py-1 text-center border-l border-slate-100 cursor-pointer hover:bg-blue-50 ${sundayBg} ${cls} ${cellExtra} ${overrideExtra} ${offHolidayExtra} ${sandwichExtra}`}
                                title={finalTitle}
                                data-testid={`grid-cell-${e.punch_id}-${d}-${row.key}`}
                              >
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  );
                })}
            </table>
          </div>
          <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex flex-wrap gap-4">
            <span><span className="inline-block w-3 h-3 ring-2 ring-red-300 align-middle mr-1"></span>Anomaly</span>
            <span><span className="inline-block w-3 h-3 border-l-2 border-blue-400 align-middle mr-1"></span>Override</span>
            <span><span className="inline-block w-3 h-3 bg-purple-50 align-middle mr-1"></span>Holiday</span>
            <span><span className="inline-block w-3 h-3 bg-slate-200 align-middle mr-1"></span>Off</span>
          </div>
        </div>
      )}

      {/* Override dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Override day {editing?.day}</DialogTitle>
            <DialogDescription>
              {editing?.emp.name} ({editing?.emp.punch_id})
            </DialogDescription>
          </DialogHeader>
          {editing && <OverrideForm daily={editing.daily} onSave={onSaveOverride} busy={busy} />}
        </DialogContent>
      </Dialog>

      {/* Mark day dialog */}
      <Dialog open={!!markDay} onOpenChange={(v) => !v && setMarkDay(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Bulk mark a day</DialogTitle>
            <DialogDescription>
              Apply across all {employees.length} employees in this upload.
            </DialogDescription>
          </DialogHeader>
          {markDay && (
            <MarkDayForm
              initialDay={markDay.day}
              dayCols={dayCols}
              onMark={onMarkDay}
              busy={busy}
              onClose={() => setMarkDay(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Pre-Finalize Preview modal */}
      <PreviewFinalizeModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        uploadId={id}
      />
    </div>
  );
}

function OverrideForm({ daily, onSave, busy }) {
  const [grade, setGrade] = useState(daily.grade || "");
  const [otMinutes, setOtMinutes] = useState(daily.ot_minutes || 0);
  const [reason, setReason] = useState(daily.override_reason || "");
  const [isHoliday, setIsHoliday] = useState(!!daily.is_holiday);
  const [isOff, setIsOff] = useState(!!daily.is_off);
  const [shortLeaveBusy, setShortLeaveBusy] = useState(false);

  const toggleShortLeave = async (checked) => {
    setShortLeaveBusy(true);
    try {
      await api.post(`/attendance/daily/${daily.id}/short-leave`, {
        is_short_leave: checked,
        reason: reason || (checked ? "Short leave" : null),
      });
      toast.success(checked ? "Marked as Short Leave" : "Short Leave removed");
      // Force parent refresh by updating local — caller will re-fetch on Save
      daily.is_short_leave = checked;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setShortLeaveBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 gap-2">
        <div>First IN: <span className="font-mono">{daily.first_in || "—"}</span></div>
        <div>Last Out: <span className="font-mono">{daily.last_out || "—"}</span></div>
        <div>Worked: <span className="font-mono">{daily.worked_minutes ? `${Math.floor(daily.worked_minutes / 60)}h ${daily.worked_minutes % 60}m` : "—"}</span></div>
        <div>Machine: <span className="font-mono">{daily.machine_status_original || "—"}</span></div>
      </div>
      {daily.anomaly_reason && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {daily.anomaly_reason}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Grade</Label>
          <Select value={grade || "none"} onValueChange={(v) => setGrade(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="override-grade"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— (clear)</SelectItem>
              <SelectItem value="P">P (Present)</SelectItem>
              <SelectItem value="H">H (Half)</SelectItem>
              <SelectItem value="A">A (Absent)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>OT minutes</Label>
          <Input
            data-testid="override-ot"
            type="number"
            min="0"
            value={otMinutes}
            onChange={(e) => setOtMinutes(e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isHoliday} onChange={(e) => setIsHoliday(e.target.checked)} />
          Holiday
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isOff} onChange={(e) => setIsOff(e.target.checked)} />
          Off day (excluded from count)
        </label>
        <label className="flex items-center gap-2 text-sm" title="Marks this day as a Short Leave (1hr-late or 1hr-early-out). Excluded from the late-count. Cap: 2 per employee per month.">
          <input
            type="checkbox"
            data-testid="override-short-leave"
            disabled={shortLeaveBusy}
            checked={!!daily.is_short_leave}
            onChange={(e) => toggleShortLeave(e.target.checked)}
          />
          Short Leave (1hr late/early — max 2/mo)
        </label>
      </div>
      <div>
        <Label>Reason</Label>
        <Input
          data-testid="override-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Approved leave / Diwali holiday"
          required
        />
      </div>
      <DialogFooter>
        <Button
          data-testid="override-save-btn"
          disabled={!reason || busy}
          onClick={() =>
            onSave({
              grade: grade || null,
              ot_minutes: Number(otMinutes) || 0,
              is_holiday: isHoliday,
              is_off: isOff,
              reason,
            })
          }
          className="brand-bg brand-bg-hover text-white"
        >
          Save override
        </Button>
      </DialogFooter>
    </div>
  );
}

function MarkDayForm({ initialDay, dayCols, onMark, busy, onClose }) {
  const [day, setDay] = useState(initialDay);
  const [mode, setMode] = useState("holiday");
  const [reason, setReason] = useState("");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Day of month</Label>
          <Select value={String(day)} onValueChange={(v) => setDay(Number(v))}>
            <SelectTrigger data-testid="mark-day-day"><SelectValue /></SelectTrigger>
            <SelectContent>
              {dayCols.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Action</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="mark-day-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="holiday">
                <PartyPopper className="h-3 w-3 inline mr-1" /> Company Holiday (P, OT=0)
              </SelectItem>
              <SelectItem value="off">
                <CalendarOff className="h-3 w-3 inline mr-1" /> Sunday / Off (excluded)
              </SelectItem>
              <SelectItem value="working">Restore to Working</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Reason (optional)</Label>
        <Input
          data-testid="mark-day-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Diwali, Sunday, Republic Day"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          data-testid="mark-day-confirm"
          disabled={busy}
          onClick={() => onMark(mode, reason)}
          className="brand-bg brand-bg-hover text-white"
        >
          Apply to all employees
        </Button>
      </DialogFooter>
    </div>
  );
}
